import React, { FC, useEffect, useState } from 'react';

import { DownOutlined, UpOutlined } from '@ant-design/icons';
import { Button, Checkbox, Col, Row, Tag } from 'antd';
import { FormattedMessage, useIntl } from 'react-intl';
import { connect } from 'react-redux';
import { AnyAction, bindActionCreators, Dispatch } from 'redux';
import { getCategoriesByChallengeType } from 'services/api';
import { fetchCategories } from 'src/actions/appActions';
import { IRootReducers } from 'src/reducers';
import { ChallengeTypeEnum } from 'src/shared/enums';
import { MaterialsEnum } from 'src/shared/enums/marketingMaterials.enum';
import { UserStatusEnum } from 'src/shared/enums/user.enum';
import { ChallengeDetail, ICategory } from 'src/shared/models';

const {
  ACCOUNT_NOT_VALIDATED,
  MANAGER_NOT_VALIDATED,
  NO_CENTER,
  HCP_NOT_VALIDATED,
  STAFF_NOT_VALIDATED,
} = UserStatusEnum;

const MOBILE_TAGS_LIMIT = 3;

const { VIDEOS, VIDEOS_YOUTUBE } = MaterialsEnum;

interface OwnProps {
  challengeList?: ChallengeDetail[];
  categoriesMM?: ICategory[];
  challengeType?: ChallengeTypeEnum;
  marketingMaterial?: boolean;
  materialTypes: string[];
  setChallengeList?: any;
  updateData?: (filters: string[], categoryFilters: string[]) => any;
}

type Props = OwnProps &
  ReturnType<typeof mapStateToProps> &
  ReturnType<typeof mapDispatchToProps>;

const ChallengeFilter: FC<Props> = ({
  categoryList,
  categoriesMM = [],
  challengeList,
  challengeType,
  fetchCategories,
  impersonate,
  marketingMaterial = false,
  materialTypes,
  setChallengeList,
  updateData,
  userStatus,
}): JSX.Element => {
  const [isShadowActive, setIsShadowActive] = useState<boolean>(false);
  const [showMoreTags, setShowMoreTags] = useState(false);
  const [filtersSelected, setFiltersSelected] = useState(false);
  const [offsetTop, setOffsetTop] = useState<number>(0);
  const [categoryFilters, setCategoryFilters] = useState<string[]>([]);
  const [filters, setFilters] = useState<string[]>([]);

  const [challengeCategories, setChallengeCategories] = useState<number[]>([]);
  const [categories, setCategories] = useState<ICategory[]>(categoriesMM);

  const { formatMessage } = useIntl();

  // handlers
  const handlerOffsetTop = () => {
    setTimeout(() => {
      const top =
        document.getElementById('hided-by-filter').getBoundingClientRect().top -
        20;
      setOffsetTop(top);
    }, 50);
  };

  const clearFilters = () => {
    setFilters([]);
    setCategoryFilters([]);
  };

  const activeShadow = () => {
    setIsShadowActive(true);
    document.body.classList.add('not-overflow');
  };

  const deactivateShadow = () => {
    setIsShadowActive(false);
    document.body.classList.remove('not-overflow');
  };

  // functions
  const handleChangeType = (type: string) => {
    const materialType =
      type === MaterialsEnum[VIDEOS_YOUTUBE].toString()
        ? VIDEOS.toString()
        : type;

    if (filters.includes(materialType)) {
      const filteredItems = filters.filter(
        (filteredTypes) => filteredTypes !== materialType
      );
      setFilters(filteredItems);
    } else {
      setFilters([...filters, materialType]);
    }
  };

  const handleChangeCategory = (category: ICategory) => {
    const id = category.value.toString();

    if (categoryFilters.includes(id)) {
      const filteredItems = categoryFilters.filter(
        (filteredTypes) => filteredTypes !== id
      );
      setCategoryFilters(filteredItems);
    } else {
      setCategoryFilters([...categoryFilters, id]);
    }
  };

  // modules
  const renderHeadboard: JSX.Element = (
    <Row
      className={`filter-headboard-content ${
        filtersSelected ? '' : 'not-filters-selected'
      }`}
      align="middle"
    >
      <Row className="show-borders">
        <Col>
          <Row
            className="filter-title"
            align="middle"
            justify="space-between"
            onClick={() =>
              isShadowActive ? deactivateShadow() : activeShadow()
            }
          >
            <FormattedMessage id="page.marketing-materials.filter" />
            <a
              className={`icon icon--arrow-dropdown-${
                isShadowActive ? 'up' : 'down'
              }`}
            />
          </Row>
        </Col>
      </Row>
    </Row>
  );

  const renderFiltersModal = (
    <Row className="container filter-modal-content">
      <Row justify="center" className="filter-buttons-mobile">
        <Col span={12}>
          <Row>
            <Button
              className="btn-primary filter-buttons-mobile--button-clear"
              onClick={clearFilters}
            >
              <FormattedMessage id="button.clear-all" />
            </Button>
          </Row>
        </Col>
        <Col span={12}>
          <Row>
            <Button
              className="btn-primary btn-primary--black filter-buttons-mobile--button-done"
              onClick={deactivateShadow}
            >
              <FormattedMessage id="button.done" />
            </Button>
          </Row>
        </Col>
      </Row>
      <div className="filter-scroll-content">
        {!materialTypes?.length ? null : (
          <>
            <Row className="filter-title">
              <FormattedMessage id="page.marketing-materials.type-content" />
            </Row>
            <Row style={{ paddingBottom: '1cm' }}>
              {materialTypes.map((type, index) => {
                return (
                  <Col xs={24} md={6} key={`material-type-${index}`}>
                    <Row justify="start">
                      <Checkbox
                        checked={filters.includes(type)}
                        className="filter-modal-content__checkbox"
                        key={`material-type-checkbox${index}`}
                        onChange={() => handleChangeType(type)}
                      >
                        <FormattedMessage
                          id={`page.marketing-materials.section-${MaterialsEnum[
                            type
                          ].toLowerCase()}`}
                        />
                      </Checkbox>
                    </Row>
                  </Col>
                );
              })}
            </Row>
          </>
        )}
        {!categories?.length ? null : (
          <>
            <Row className="filter-title filter-title-brand">
              {formatMessage({ id: 'page.marketing-materials.product' })}
            </Row>
            <Row style={{ paddingBottom: '1cm' }}>
              {categories?.map((category: ICategory, index: number) => {
                return (
                  <Col xs={24} md={6} key={`$brand-${index}`}>
                    <Row justify="start">
                      <Checkbox
                        checked={categoryFilters.includes(
                          category.value.toString()
                        )}
                        className="filter-modal-content__checkbox"
                        key={`brand-checkbox-${index}`}
                        onChange={() => handleChangeCategory(category)}
                      >
                        {category.description}
                      </Checkbox>
                    </Row>
                  </Col>
                );
              })}
            </Row>
          </>
        )}
      </div>
      <Row justify="end" className="filter-buttons">
        <Button
          className="btn-primary"
          onClick={clearFilters}
          style={{ marginRight: '1cm' }}
        >
          <FormattedMessage id="button.clear-all" />
        </Button>
        <Button
          className="btn-primary btn-primary--black"
          onClick={deactivateShadow}
        >
          <FormattedMessage id="button.done" />
        </Button>
      </Row>
    </Row>
  );

  const renderTagsList = (
    filters?: string[],
    categoryFilters?: string[],
    mobile?: boolean
  ) => {
    return filters?.length || categoryFilters?.length ? (
      <div className="container">
        <Row
          justify="start"
          className={
            mobile ? 'filter-tags' : 'filter-tags filter-tags__desktop'
          }
        >
          {filters && renderFilterTags(filters)}
          {categoryFilters && renderCategoryTags(categoryFilters)}
        </Row>
      </div>
    ) : null;
  };

  const renderCategoryTags = (categoryArr: string[]) => {
    const tag = (category: string, key: number) => {
      const selectedCategory: ICategory = categories.find(
        (_category) => _category.value.toString() === category
      );

      return (
        <Tag
          closable
          key={`tag-brand-${key}`}
          onClose={() => handleChangeCategory(selectedCategory)}
          className="filter-tags__tag"
        >
          {selectedCategory.description}
        </Tag>
      );
    };

    return categoryArr.map((category: string, key: number) =>
      tag(category, key)
    );
  };

  const renderFilterTags = (filterArr: string[]) => {
    const tag = (f: string, key: number) => {
      return (
        <Tag
          closable
          key={`tag-brand-filter-${key}`}
          onClose={() => handleChangeType(f)}
          className="filter-tags__tag"
        >
          {MaterialsEnum[f]}
        </Tag>
      );
    };
    return filterArr.map((f: string, key: number) => tag(f, key));
  };

  const renderMobileTags = (filters?: string[], categoryFilters?: string[]) => {
    const moreTags = filters?.length + categoryFilters?.length > 3;
    const initialTags: JSX.Element[] = filters ? renderFilterTags(filters) : [];

    const categoryArr = [...categoryFilters];
    categoryArr.length = MOBILE_TAGS_LIMIT - initialTags.length;

    initialTags.push(...renderCategoryTags(categoryArr));
    const filterTags = [
      ...renderFilterTags(filters),
      ...renderCategoryTags(categoryFilters),
    ];

    return (
      <Row
        align="top"
        justify="space-between"
        className="filter-tags__mobile--container"
      >
        {moreTags ? (
          <div
            className="filter-tags__mobile--more"
            onClick={() => setShowMoreTags(!showMoreTags)}
          >
            {showMoreTags ? 'Less' : 'More'}
            {showMoreTags ? <UpOutlined /> : <DownOutlined />}
          </div>
        ) : null}
        {!showMoreTags && initialTags}
        {showMoreTags && filterTags}
      </Row>
    );
  };

  const modalContainerClassName =
    impersonate ||
    [
      ACCOUNT_NOT_VALIDATED,
      MANAGER_NOT_VALIDATED,
      NO_CENTER,
      HCP_NOT_VALIDATED,
      STAFF_NOT_VALIDATED,
    ].includes(userStatus)
      ? 'custom-filter-shadow__container-low'
      : 'custom-filter-shadow__container';

  useEffect(() => {
    setFiltersSelected(filters?.length > 0 || categoryFilters?.length > 0);
    updateData && updateData(filters, categoryFilters);
    handlerOffsetTop();
    if (marketingMaterial) return;

    if (categoryFilters.length == 0) {
      setChallengeList(challengeList);
      return;
    }

    const filteredTrainings = challengeList.filter((training) => {
      let isIncluded = false;
      const trainingCategories = training?.challengeCategories?.map(
        (category) => category.toString()
      );
      if (!trainingCategories?.length) return false;

      categoryFilters.forEach((request) => {
        if (trainingCategories.includes(request)) isIncluded = true;
      });
      return isIncluded;
    });

    setChallengeList(filteredTrainings);
  }, [filters, categoryFilters, isShadowActive, challengeList]);

  useEffect(() => {
    if (marketingMaterial) return;

    async function getCategories() {
      const response = await getCategoriesByChallengeType(challengeType, {});

      setChallengeCategories(response.data);
    }

    fetchCategories();
    getCategories();
  }, []);

  useEffect(() => {
    if (categories.length == 0 && categoryList != undefined) {
      setCategories(
        categoryList.filter((category) =>
          challengeCategories.includes(category.value)
        )
      );
    }
  }, [categoryList, challengeCategories]);

  return (
    <>
      <div className="custom-filter container container--no-padding">
        {renderHeadboard}

        {renderTagsList(filters, categoryFilters)}
        {filtersSelected && (
          <Row justify="start" className="filter-tags filter-tags__mobile">
            {renderMobileTags(filters, categoryFilters)}
          </Row>
        )}
      </div>

      {!isShadowActive ? null : (
        <div
          id="custom-filter-shadow"
          className={`custom-filter-shadow ${
            marketingMaterial ? 'marketing-material-filter' : ''
          }`}
          style={{ top: offsetTop }}
        >
          <div className={modalContainerClassName}>
            <div className="custom-filter-shadow__button">
              <FormattedMessage id="page.marketing-materials.filter" />

              <div
                className="icon icon--close custom-filter-shadow__button--close"
                onClick={deactivateShadow}
              />
            </div>
            {renderFiltersModal}
          </div>
        </div>
      )}
    </>
  );
};

const mapStateToProps = (state: IRootReducers) => {
  return {
    impersonate: state.auth.impersonate,
    userStatus: state.auth.userStatus,
    categoryList: state.app.categoryList,
  };
};
const mapDispatchToProps = (dispatch: Dispatch<AnyAction>) =>
  bindActionCreators({ fetchCategories }, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(ChallengeFilter);
